import { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "@/components/capacity/interactive-legend";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getDateRangeForPeriod, formatDateRange } from "@/lib/date-utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpandableTableRows } from "@/components/ui/expandable-table-rows";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  AlertCircle,
  BarChart3,
  TrendingUp,
  Users,
  Grid3X3,
  Info,
  CheckCircle2,
  ExternalLink,
  X,
  BarChart2,
  Activity,
  List,
} from "lucide-react";
import {
  useBehaviorScoreTrend,
  useBehaviorTopScores,
  useLocationTrends,
  useSilentAnimals,
  useAssessedAnimalList,
  useCoverageTrend,
  useSilentAnimalList,
  type TopScoreRow,
  type CoverageTimePoint,
  type SilentAnimalRow,
} from "@/hooks/use-behavior-analytics";
import { InsightsSectionNav, type SectionDef } from "@/components/capacity/insights-section-nav";
import { formatTrendDate } from "@/lib/trend-comparison";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { BehaviorAlertsSummary } from "@/components/behavior-alerts-summary";

// ─── Constants ────────────────────────────────────────────────────────────────

type ComparisonMode = "none" | "wow" | "mom";
type ChartType = "bar" | "area";
type GroupMode = "code" | "band";
type MetricMode = "interactions" | "animals";
type LocationMetric = "redPct" | "interactions";

const COMPARISON_OPTIONS: { value: ComparisonMode; label: string; fullLabel: string; days: number }[] = [
  { value: "none", label: "No compare", fullLabel: "None", days: 0 },
  { value: "wow", label: "Wk/Wk", fullLabel: "Week over Week", days: 7 },
  { value: "mom", label: "Mo/Mo", fullLabel: "Month over Month", days: 30 },
];

const CODE_ORDER = ["1", "2", "3A", "3B", "4A", "4B", "5A", "5B", "UNSCORED"];
const BAND_ORDER = ["GREEN", "YELLOW", "RED", "UNSCORED"];

const CODE_COLORS: Record<string, string> = {
  "1": "hsl(142, 71%, 45%)",
  "2": "hsl(142, 69%, 58%)",
  "3A": "hsl(45, 93%, 47%)",
  "3B": "hsl(25, 95%, 53%)",
  "4A": "hsl(0, 86%, 68%)",
  "4B": "hsl(0, 84%, 60%)",
  "5A": "hsl(0, 72%, 51%)",
  "5B": "hsl(0, 74%, 42%)",
  "UNSCORED": "hsl(215, 20%, 65%)",
};

const BAND_COLORS: Record<string, string> = {
  GREEN: "hsl(142, 71%, 45%)",
  YELLOW: "hsl(45, 93%, 47%)",
  RED: "hsl(0, 84%, 60%)",
  UNSCORED: "hsl(215, 20%, 65%)",
};

const LOCATION_LINE_COLORS = [
  "hsl(220, 70%, 50%)",
  "hsl(25, 95%, 53%)",
  "hsl(280, 65%, 55%)",
  "hsl(142, 60%, 45%)",
  "hsl(340, 65%, 55%)",
  "hsl(190, 80%, 45%)",
];

const BEHAVIOR_SECTIONS_SYSTEM: SectionDef[] = [
  { id: "section-score-overview", label: "Overview", group: "Scores" },
  { id: "section-score-trend", label: "Behavior Score Trend", group: "Scores" },
  { id: "section-health-trend", label: "Behavior Health Trend", group: "Scores" },
  { id: "section-location-trend", label: "Location Trends", group: "Monitoring" },
  { id: "section-interaction-volume", label: "Interaction Volume", group: "Monitoring" },
  { id: "section-score-breakdown", label: "Behavior Score Breakdown", group: "Monitoring" },
  { id: "section-alerts-summary", label: "Signals", group: "Monitoring" },
];

const BEHAVIOR_SECTIONS_LOCATION: SectionDef[] = [
  { id: "section-score-overview", label: "Overview", group: "Scores" },
  { id: "section-score-trend", label: "Behavior Score Trend", group: "Scores" },
  { id: "section-health-trend", label: "Behavior Health Trend", group: "Scores" },
  { id: "section-interaction-volume", label: "Interaction Volume", group: "Monitoring" },
  { id: "section-score-breakdown", label: "Behavior Score Breakdown", group: "Monitoring" },
  { id: "section-alerts-summary", label: "Signals", group: "Monitoring" },
];

// ─── Helper: band badge ───────────────────────────────────────────────────────

function ScoreBandBadge({ band }: { band: string | null }) {
  if (!band) return <span className="text-muted-foreground text-xs">—</span>;
  const colorMap: Record<string, string> = {
    GREEN: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    YELLOW: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    RED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };
  return (
    <Badge variant="outline" className={`text-xs ${colorMap[band] ?? "bg-muted text-muted-foreground"}`}>
      {band}
    </Badge>
  );
}

// ─── Band Summary Bar ─────────────────────────────────────────────────────────

function BandSummaryBar({
  scores,
  metricMode,
  isLoading,
}: {
  scores: TopScoreRow[];
  metricMode: MetricMode;
  isLoading: boolean;
}) {
  const totals = useMemo(() => {
    if (!scores || scores.length === 0) return null;
    const getValue = (s: TopScoreRow) =>
      metricMode === "interactions" ? s.totalInteractions : s.totalAnimals;
    const byBand: Record<string, number> = { GREEN: 0, YELLOW: 0, RED: 0, UNSCORED: 0 };
    let total = 0;
    for (const s of scores) {
      const band = s.scoreBand ?? "UNSCORED";
      byBand[band] = (byBand[band] ?? 0) + getValue(s);
      total += getValue(s);
    }
    if (total === 0) return null;
    return BAND_ORDER.map((band) => ({
      band,
      count: byBand[band] ?? 0,
      pct: Math.round(((byBand[band] ?? 0) / total) * 100),
    })).filter((b) => b.pct > 0);
  }, [scores, metricMode]);

  if (isLoading) return <Skeleton className="h-7 w-full rounded-md" />;
  if (!totals) return null;

  return (
    <div
      className="flex h-7 rounded-md overflow-hidden text-xs font-medium"
      data-testid="band-summary-bar"
    >
      {totals.map(({ band, pct }) => (
        <div
          key={band}
          style={{ width: `${pct}%`, backgroundColor: BAND_COLORS[band] }}
          className="flex items-center justify-center text-white overflow-hidden"
          title={`${band}: ${pct}%`}
        >
          {pct >= 10 && `${pct}%`}
        </div>
      ))}
    </div>
  );
}

// ─── Overview Stat Cards ──────────────────────────────────────────────────────

function OverviewStatCards({
  topScoresData,
  isLoading,
  dateRangeLabel,
  metricMode,
  scopeLabel,
  isSystemView,
  showHints,
  silentData,
  silentLoading,
}: {
  topScoresData: ReturnType<typeof useBehaviorTopScores>["data"];
  isLoading: boolean;
  dateRangeLabel: string;
  metricMode: MetricMode;
  scopeLabel: string;
  isSystemView: boolean;
  showHints: boolean;
  silentData: { totalInCare: number; noRecentInteractions: number; pct: number } | undefined;
  silentLoading: boolean;
}) {
  const bandStats = useMemo(() => {
    if (!topScoresData?.scores) return null;
    const getValue = (s: TopScoreRow) =>
      metricMode === "interactions" ? s.totalInteractions : s.totalAnimals;
    const byBand: Record<string, number> = { GREEN: 0, YELLOW: 0, RED: 0, UNSCORED: 0 };
    let total = 0;
    for (const s of topScoresData.scores) {
      const band = s.scoreBand ?? "UNSCORED";
      byBand[band] = (byBand[band] ?? 0) + getValue(s);
      total += getValue(s);
    }
    if (total === 0) return null;
    const pct = (band: string) => Math.round(((byBand[band] ?? 0) / total) * 100);
    return { greenPct: pct("GREEN"), yellowPct: pct("YELLOW"), redPct: pct("RED"), total };
  }, [topScoresData, metricMode]);

  const metricLabel = metricMode === "interactions" ? "interactions" : "animals";
  const totalValue = metricMode === "interactions"
    ? topScoresData?.totalInteractions
    : topScoresData?.scores.reduce((s, r) => s + r.totalAnimals, 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total {metricMode === "interactions" ? "Interactions" : "Animals"}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-interactions">
              {isLoading ? <Skeleton className="h-8 w-20" /> : (totalValue?.toLocaleString() ?? "0")}
            </div>
            <p className="text-xs text-muted-foreground">{dateRangeLabel}</p>
            {showHints && (
              <p className="mt-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The total number of behavior interactions recorded in this period. In Animals mode this switches to distinct animals assessed, with each individual counted once regardless of how many interactions they had.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-400">% GREEN</CardTitle>
            <div className="w-3 h-3 rounded-full bg-green-500 shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="text-green-pct">
              {isLoading ? <Skeleton className="h-8 w-16" /> : bandStats ? `${bandStats.greenPct}%` : "—"}
            </div>
            <p className="text-xs text-muted-foreground">Scores 1–2</p>
            {showHints && (
              <p className="mt-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The share of interactions (or animals, in Animals mode) that received a score of 1 or 2 during this period.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-400">% YELLOW</CardTitle>
            <div className="w-3 h-3 rounded-full bg-yellow-500 shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400" data-testid="text-yellow-pct">
              {isLoading ? <Skeleton className="h-8 w-16" /> : bandStats ? `${bandStats.yellowPct}%` : "—"}
            </div>
            <p className="text-xs text-muted-foreground">Scores 3A–3B</p>
            {showHints && (
              <p className="mt-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The share of interactions (or animals, in Animals mode) that received a score of 3A or 3B during this period.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700 dark:text-red-400">% RED</CardTitle>
            <div className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700 dark:text-red-400" data-testid="text-red-pct">
              {isLoading ? <Skeleton className="h-8 w-16" /> : bandStats ? `${bandStats.redPct}%` : "—"}
            </div>
            <p className="text-xs text-muted-foreground">Scores 4A–5B</p>
            {showHints && (
              <p className="mt-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The share of interactions (or animals, in Animals mode) that received a score of 4A, 4B, 5A, or 5B during this period.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-400">Not Logged (Dogs)</CardTitle>
            <Activity className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="text-not-logged">
              {silentLoading ? <Skeleton className="h-8 w-16" /> : silentData != null ? silentData.noRecentInteractions.toLocaleString() : "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              {silentLoading ? <Skeleton className="h-3 w-20 mt-1" /> : silentData != null ? `of ${silentData.totalInCare.toLocaleString()} dogs in care` : "dogs in care"}
            </p>
            {showHints && (
              <p className="mt-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                Dogs currently in care with no behavior interaction recorded within the selected date range.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Band distribution — {dateRangeLabel}</p>
        <BandSummaryBar scores={topScoresData?.scores ?? []} metricMode={metricMode} isLoading={isLoading} />
      </div>
    </div>
  );
}

// ─── Score Trend Chart ────────────────────────────────────────────────────────

function ScoreTrendChart({
  rows,
  isLoading,
  chartType,
  groupMode,
  metricMode,
}: {
  rows: ReturnType<typeof useBehaviorScoreTrend>["data"] extends { rows: infer R } | undefined ? (R extends (infer T)[] ? T : never)[] : never;
  isLoading: boolean;
  chartType: ChartType;
  groupMode: GroupMode;
  metricMode: MetricMode;
}) {
  const { chartData, seriesKeys } = useMemo(() => {
    if (!rows || rows.length === 0) return { chartData: [], seriesKeys: [] };

    const dateMap = new Map<string, Record<string, number>>();
    const keySet = new Set<string>();

    for (const row of rows) {
      const key = groupMode === "code" ? row.scoreCode : (row.scoreBand ?? "UNSCORED");
      const val = metricMode === "interactions" ? row.interactionCount : row.animalDistinctCount;
      keySet.add(key);
      if (!dateMap.has(row.snapshotDate)) dateMap.set(row.snapshotDate, {});
      dateMap.get(row.snapshotDate)![key] = (dateMap.get(row.snapshotDate)![key] ?? 0) + val;
    }

    const order = groupMode === "code" ? CODE_ORDER : BAND_ORDER;
    const seriesKeys = order.filter((k) => keySet.has(k));

    const chartData = Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, formattedDate: formatTrendDate(date), ...vals }));

    return { chartData, seriesKeys };
  }, [rows, groupMode, metricMode]);

  const tickInterval = useResponsiveTickInterval(chartData.length);
  const colorMap = groupMode === "code" ? CODE_COLORS : BAND_COLORS;

  const allSeries = useMemo<LegendSeries[]>(
    () => seriesKeys.map((key) => ({ key, label: key, color: colorMap[key] ?? "hsl(var(--muted-foreground))" })),
    [seriesKeys, groupMode]
  );
  const { visibleSeries, toggleSeries } = useToggleableSeries(allSeries);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const raw = payload[0]?.payload?.date;
    const dateLabel = raw ? format(parseISO(raw), "MMM d, yyyy") : label;
    return (
      <div className="rounded-md border bg-background p-2 text-xs shadow-md">
        <p className="font-medium mb-1">{dateLabel}</p>
        {payload.map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.fill ?? entry.stroke }} />
            <span className="text-muted-foreground">{entry.dataKey}:</span>
            <span className="font-medium">{entry.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) return <Skeleton className="h-56 w-full" />;
  if (chartData.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <BarChart3 className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">No trend data for this period</p>
        </div>
      </div>
    );
  }

  const sharedProps = {
    data: chartData,
    margin: { top: 4, right: 4, left: -16, bottom: 0 },
  };

  const commonAxis = (
    <>
      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
      <XAxis dataKey="formattedDate" tick={{ fontSize: 11 }} className="text-muted-foreground" interval={tickInterval} />
      <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" />
      <Tooltip content={<CustomTooltip />} />
    </>
  );

  if (chartType === "area") {
    return (
      <div>
        <InteractiveLegend
          series={allSeries}
          visibleSeries={visibleSeries}
          onToggle={toggleSeries}
          compact
          testIdPrefix="behavior-trend"
        />
        <ResponsiveContainer width="100%" height={224}>
          <AreaChart {...sharedProps}>
            {commonAxis}
            {allSeries.filter((s) => visibleSeries.has(s.key)).map((s) => (
              <Area
                key={s.key}
                type="linear"
                dataKey={s.key}
                stackId="a"
                fill={s.color}
                stroke="none"
                fillOpacity={0.85}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div>
      <InteractiveLegend
        series={allSeries}
        visibleSeries={visibleSeries}
        onToggle={toggleSeries}
        compact
        testIdPrefix="behavior-trend"
      />
      <ResponsiveContainer width="100%" height={224}>
        <BarChart {...sharedProps}>
          {commonAxis}
          {allSeries.filter((s) => visibleSeries.has(s.key)).map((s) => (
            <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.color} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Health Trend Chart ───────────────────────────────────────────────────────

function HealthTrendChart({
  rows,
  isLoading,
}: {
  rows: ReturnType<typeof useBehaviorScoreTrend>["data"] extends { rows: infer R } | undefined ? (R extends (infer T)[] ? T : never)[] : never;
  isLoading: boolean;
}) {
  const chartData = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const dateMap = new Map<string, { total: number; red: number }>();
    for (const row of rows) {
      if (!dateMap.has(row.snapshotDate)) dateMap.set(row.snapshotDate, { total: 0, red: 0 });
      const entry = dateMap.get(row.snapshotDate)!;
      entry.total += row.interactionCount;
      if (row.scoreBand === "RED") entry.red += row.interactionCount;
    }
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { total, red }]) => ({
        date,
        formattedDate: formatTrendDate(date),
        redPct: total > 0 ? Math.round((red / total) * 1000) / 10 : 0,
      }));
  }, [rows]);

  const tickInterval = useResponsiveTickInterval(chartData.length);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const raw = payload[0]?.payload?.date;
    const dateLabel = raw ? format(parseISO(raw), "MMM d, yyyy") : "";
    return (
      <div className="rounded-md border bg-background p-2 text-xs shadow-md">
        <p className="font-medium mb-1">{dateLabel}</p>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full shrink-0 bg-red-500" />
          <span className="text-muted-foreground">% RED:</span>
          <span className="font-medium">{payload[0]?.value?.toFixed(1)}%</span>
        </div>
      </div>
    );
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Activity className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">No health trend data for this period</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={192}>
      <LineChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="formattedDate" tick={{ fontSize: 11 }} className="text-muted-foreground" interval={tickInterval} />
        <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="monotone"
          dataKey="redPct"
          stroke={BAND_COLORS.RED}
          strokeWidth={2}
          dot={false}
          name="% RED"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Per-Location Trend Chart ─────────────────────────────────────────────────

function LocationTrendChart({
  locationTrendsData,
  isLoading,
  locationMetric,
}: {
  locationTrendsData: ReturnType<typeof useLocationTrends>["data"];
  isLoading: boolean;
  locationMetric: LocationMetric;
}) {
  const { chartData, locations, locationColors } = useMemo(() => {
    if (!locationTrendsData?.series || locationTrendsData.series.length === 0) {
      return { chartData: [], locations: [], locationColors: {} as Record<string, string> };
    }
    const locs = locationTrendsData.locations;
    const locationColors: Record<string, string> = {};
    locs.forEach((loc, i) => {
      locationColors[loc] = LOCATION_LINE_COLORS[i % LOCATION_LINE_COLORS.length];
    });
    const chartData = locationTrendsData.series.map((point) => {
      const row: Record<string, any> = { date: point.date, formattedDate: formatTrendDate(point.date) };
      for (const loc of locs) {
        const locData = point.locations[loc];
        row[loc] = locationMetric === "redPct" ? (locData?.redPct ?? null) : (locData?.interactions ?? null);
      }
      return row;
    });
    return { chartData, locations: locs, locationColors };
  }, [locationTrendsData, locationMetric]);

  const allLocationSeries = useMemo<LegendSeries[]>(
    () => locations.map((loc) => ({ key: loc, label: loc, color: locationColors[loc] ?? "hsl(var(--muted-foreground))" })),
    [locations, locationColors]
  );
  const { visibleSeries: visibleLocations, toggleSeries: toggleLocation } = useToggleableSeries(allLocationSeries);

  const tickInterval = useResponsiveTickInterval(chartData.length);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const raw = payload[0]?.payload?.date;
    const dateLabel = raw ? format(parseISO(raw), "MMM d, yyyy") : "";
    return (
      <div className="rounded-md border bg-background p-2 text-xs shadow-md">
        <p className="font-medium mb-1">{dateLabel}</p>
        {payload.map((entry: any, i: number) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.stroke }} />
            <span className="text-muted-foreground">{entry.dataKey}:</span>
            <span className="font-medium">
              {locationMetric === "redPct" ? `${entry.value?.toFixed(1)}%` : entry.value?.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (chartData.length === 0 || locations.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <BarChart2 className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">No per-location data available</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <InteractiveLegend
        series={allLocationSeries}
        visibleSeries={visibleLocations}
        onToggle={toggleLocation}
        compact
        testIdPrefix="behavior-location"
      />
      <ResponsiveContainer width="100%" height={192}>
        <LineChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 11 }} className="text-muted-foreground" interval={tickInterval} />
          <YAxis
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            tickFormatter={locationMetric === "redPct" ? (v) => `${v}%` : undefined}
          />
          <Tooltip content={<CustomTooltip />} />
          {allLocationSeries.filter((s) => visibleLocations.has(s.key)).map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Score Breakdown ──────────────────────────────────────────────────────────

function ScoreBreakdown({
  scores,
  isLoading,
  metricMode,
  dateRangeLabel,
  showHints,
  scopeLabel,
}: {
  scores: TopScoreRow[];
  isLoading: boolean;
  metricMode: MetricMode;
  dateRangeLabel: string;
  showHints: boolean;
  scopeLabel: string;
}) {
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  const chartData = useMemo(() => {
    if (!scores?.length) return [];
    const totalAnimals = scores.reduce((s, r) => s + r.totalAnimals, 0);
    return CODE_ORDER
      .filter((code) => scores.some((s) => s.scoreCode === code))
      .map((code) => {
        const row = scores.find((s) => s.scoreCode === code)!;
        const pct =
          metricMode === "interactions"
            ? row.percentOfTotal
            : totalAnimals > 0
            ? Math.round((row.totalAnimals / totalAnimals) * 1000) / 10
            : 0;
        return {
          scoreCode: row.scoreCode,
          scoreBand: row.scoreBand ?? "UNSCORED",
          value: metricMode === "interactions" ? row.totalInteractions : row.totalAnimals,
          pct,
          color: CODE_COLORS[row.scoreCode] ?? "hsl(215, 20%, 65%)",
        };
      });
  }, [scores, metricMode]);

  const metricLabel = metricMode === "interactions" ? "Interactions" : "Animals";
  const chartHeight = Math.max(chartData.length * 38 + 40, 180);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="rounded-md border bg-background p-2 text-xs shadow-md">
        <p className="font-medium mb-1">Score {d.scoreCode} — {d.scoreBand}</p>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{metricLabel}:</span>
          <span className="font-medium">{d.value.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">% of total:</span>
          <span className="font-medium">{d.pct}%</span>
        </div>
      </div>
    );
  };

  const content = isLoading ? (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  ) : !scores?.length ? (
    <div className="text-center py-8 text-muted-foreground">
      <BarChart3 className="h-8 w-8 mx-auto mb-2" />
      <p>No score data available</p>
    </div>
  ) : viewMode === "chart" ? (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart layout="vertical" data={chartData} margin={{ top: 4, right: 32, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => `${v}%`}
          domain={[0, "dataMax"]}
          className="text-muted-foreground"
        />
        <YAxis
          type="category"
          dataKey="scoreCode"
          tick={{ fontSize: 11 }}
          width={36}
          className="text-muted-foreground"
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
        <Bar dataKey="pct" radius={[0, 3, 3, 0]} maxBarSize={22}>
          {chartData.map((entry) => (
            <Cell key={entry.scoreCode} fill={entry.color} fillOpacity={0.9} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  ) : (
    <Table className="table-zebra">
      <TableHeader>
        <TableRow>
          <TableHead>Score</TableHead>
          <TableHead>Band</TableHead>
          <TableHead className="text-right">Interactions</TableHead>
          <TableHead className="text-right">Animals</TableHead>
          <TableHead className="text-right">% of Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {scores.map((score, i) => (
          <TableRow
            key={score.scoreCode}
            data-testid={`row-score-${score.scoreCode}`}
            className={i % 2 === 1 ? "bg-muted/40" : ""}
          >
            <TableCell className="font-medium">{score.scoreCode}</TableCell>
            <TableCell><ScoreBandBadge band={score.scoreBand} /></TableCell>
            <TableCell className="text-right">{score.totalInteractions.toLocaleString()}</TableCell>
            <TableCell className="text-right">{score.totalAnimals.toLocaleString()}</TableCell>
            <TableCell className="text-right">{score.percentOfTotal}%</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle data-testid="text-breakdown-title">Behavior Score Breakdown</CardTitle>
              <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
            </div>
            <CardDescription>Aggregated totals by behavior score — {dateRangeLabel}</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <ToggleChip active={viewMode === "chart"} onClick={() => setViewMode("chart")} testId="toggle-breakdown-chart">
              <BarChart2 className="h-3 w-3" />
              Chart
            </ToggleChip>
            <ToggleChip active={viewMode === "table"} onClick={() => setViewMode("table")} testId="toggle-breakdown-table">
              <List className="h-3 w-3" />
              Table
            </ToggleChip>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {showHints && (
          <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
            {viewMode === "chart"
              ? "Each bar shows the aggregated count for that score code across the selected date range. In Animals mode, each animal is counted once per code rather than once per assessment."
              : "Each row shows the total interactions and distinct animals recorded at that score code, plus that code's share of all interactions. When the Interactions count for a row is higher than its Animals count, some animals received that score more than once during the period."}
          </p>
        )}
        {content}
      </CardContent>
    </Card>
  );
}

// ─── Data Banner ──────────────────────────────────────────────────────────────

// ─── Silent Animal List Table ─────────────────────────────────────────────────

function SilentAnimalTable({
  animals,
  isLoading,
  windowDays,
  mode,
  noData,
}: {
  animals: SilentAnimalRow[] | undefined;
  isLoading: boolean;
  windowDays: number;
  mode: "not-assessed" | "assessed";
  noData?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2 mt-4">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );
  }

  if (noData) return null;

  if (!animals?.length) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center py-8 text-muted-foreground">
        <CheckCircle2 className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">
          {mode === "not-assessed"
            ? `All dogs in care have been assessed in the prior ${windowDays} days`
            : `No dogs in care have been assessed in the prior ${windowDays} days`}
        </p>
      </div>
    );
  }

  const summaryLabel = mode === "not-assessed"
    ? `${animals.length} dog${animals.length !== 1 ? "s" : ""} with no interaction in the prior ${windowDays} days`
    : `${animals.length} dog${animals.length !== 1 ? "s" : ""} assessed in the prior ${windowDays} days`;

  return (
    <div className="mt-4">
      <p className="text-sm font-medium mb-3 text-muted-foreground">{summaryLabel}</p>
      <div className="overflow-x-auto rounded-md border">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Species</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Last Assessed</TableHead>
              <TableHead className="text-right">Days Since</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <ExpandableTableRows
              rows={animals}
              previewCount={5}
              colSpan={5}
              moreLabel={(n) => `View ${n} more ${n === 1 ? "animal" : "animals"}`}
              renderRow={(animal, idx) => (
                <TableRow key={animal.animalId}>
                  <TableCell>
                    <a
                      href={`/animals/${animal.animalId}`}
                      className="font-medium text-foreground hover:underline"
                      data-testid={`link-coverage-animal-${animal.animalId}`}
                    >
                      {animal.name}
                    </a>
                  </TableCell>
                  <TableCell className="text-muted-foreground capitalize">
                    {animal.species.toLowerCase()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{animal.location}</TableCell>
                  <TableCell className="text-right text-muted-foreground text-sm">
                    {animal.lastInteractionDate
                      ? format(parseISO(animal.lastInteractionDate), "MMM d, yyyy")
                      : <span className="text-amber-600 dark:text-amber-400 font-medium">Never</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {animal.daysSinceLast != null
                      ? <span className={mode === "not-assessed" && animal.daysSinceLast > 30 ? "text-red-600 dark:text-red-400 font-medium" : "text-muted-foreground"}>{animal.daysSinceLast}</span>
                      : <span className="text-amber-600 dark:text-amber-400 font-medium">—</span>}
                  </TableCell>
                </TableRow>
              )}
            />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Coverage Trend Chart ─────────────────────────────────────────────────────

function CoverageTrendChart({
  series,
  isLoading,
  windowDays,
  mode,
}: {
  series: CoverageTimePoint[] | undefined;
  isLoading: boolean;
  windowDays: number;
  mode: "not-assessed" | "assessed";
}) {
  const tickInterval = useResponsiveTickInterval(series?.length ?? 0);

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!series?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
        <BarChart3 className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">No data for this period</p>
      </div>
    );
  }

  const chartData = series.map((p) => ({
    date: p.date,
    inCare: p.inCareCount,
    amber: mode === "not-assessed" ? p.silentCount : Math.max(0, p.inCareCount - p.silentCount),
  }));

  const amberLabel = mode === "not-assessed"
    ? `No interaction (prior ${windowDays} days)`
    : `Assessed (prior ${windowDays} days)`;

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => format(parseISO(v), "M/d/yy")}
          interval={tickInterval}
          className="text-muted-foreground"
        />
        <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" allowDecimals={false} />
        <Tooltip
          labelFormatter={(v) => format(parseISO(v as string), "MMM d, yyyy")}
          formatter={(value: number, name: string) => [
            value.toLocaleString(),
            name === "inCare" ? "Dogs In Care" : amberLabel,
          ]}
          contentStyle={{ fontSize: 12 }}
        />
        <Line
          type="linear"
          dataKey="inCare"
          name="inCare"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth={1.5}
          dot={false}
          strokeDasharray="5 3"
        />
        <Line
          type="linear"
          dataKey="amber"
          name="amber"
          stroke="hsl(38 92% 50%)"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function AnalyticsDataBanner({ staleLocationCount }: { staleLocationCount?: number }) {
  const [dismissed, setDismissed] = useState(false);
  const hasStaleData = staleLocationCount && staleLocationCount > 0;

  if (!hasStaleData) {
    return (
      <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="text-analytics-data-info">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>Behavior data derived from All Pets and Interaction Summary reports</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400" data-testid="badge-analytics-report-freshness">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>All locations current</span>
        </div>
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500 relative">
      <Info className="h-4 w-4" />
      <button
        type="button"
        className="absolute top-3 right-3 p-1 rounded-sm text-blue-600 hover:text-blue-800 hover:bg-blue-200 dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-800 transition-colors"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-analytics-alert"
      >
        <X className="h-4 w-4" />
      </button>
      <AlertTitle className="text-blue-800 dark:text-blue-200 pr-8" data-testid="text-analytics-data-info-stale">
        Behavior data derived from All Pets and Interaction Summary reports
        <span className="font-normal text-amber-700 dark:text-amber-400 ml-2">
          ({staleLocationCount} location{staleLocationCount !== 1 ? "s" : ""} with stale data)
        </span>
      </AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
        <span>Upload new All Pets and Interaction Summary reports to see the latest behavior data.</span>
        <Button variant="outline" size="sm" asChild className="bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 ml-auto">
          <a href="/reports?tab=interactions" target="_blank" rel="noopener noreferrer" data-testid="link-analytics-view-interaction-reports">
            View Reports
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// ─── Comparison Stats Panel ───────────────────────────────────────────────────

function ComparisonStatsPanel({
  trendRows,
  comparisonMode,
  onModeChange,
  startDate,
  endDate,
  scopeLabel,
  isLoading,
}: {
  trendRows: ReturnType<typeof useBehaviorScoreTrend>["data"] extends { rows: infer R } | undefined ? (R extends (infer T)[] ? T : never)[] : never;
  comparisonMode: ComparisonMode;
  onModeChange: (mode: ComparisonMode) => void;
  startDate: Date;
  endDate: Date;
  scopeLabel: string;
  isLoading: boolean;
}) {
  const stats = useMemo(() => {
    const compDays = COMPARISON_OPTIONS.find((o) => o.value === comparisonMode)?.days ?? 7;

    const cStart = new Date(startDate); cStart.setHours(0, 0, 0, 0);
    const cEnd = new Date(endDate); cEnd.setHours(23, 59, 59, 999);
    const pEnd = new Date(cStart); pEnd.setDate(pEnd.getDate() - 1); pEnd.setHours(23, 59, 59, 999);
    const pStart = new Date(pEnd); pStart.setDate(pStart.getDate() - compDays + 1); pStart.setHours(0, 0, 0, 0);

    const byBand = (band: string | null, from: Date, to: Date) =>
      trendRows.reduce((s, r) => {
        const d = new Date(r.snapshotDate);
        if (d >= from && d <= to && (r.scoreBand ?? "UNSCORED") === (band ?? "UNSCORED")) return s + r.interactionCount;
        return s;
      }, 0);

    const tot = (from: Date, to: Date) =>
      trendRows.reduce((s, r) => {
        const d = new Date(r.snapshotDate);
        return d >= from && d <= to ? s + r.interactionCount : s;
      }, 0);

    const cTotal = tot(cStart, cEnd);
    const pTotal = tot(pStart, pEnd);
    const change = cTotal - pTotal;
    const pctChange = pTotal > 0 ? ((change / pTotal) * 100).toFixed(1) : "0.0";

    const bandPct = (band: string, from: Date, to: Date, total: number) =>
      total > 0 ? Math.round((byBand(band, from, to) / total) * 100) : 0;

    const cGreen = bandPct("GREEN", cStart, cEnd, cTotal);
    const pGreen = bandPct("GREEN", pStart, pEnd, pTotal);
    const cRed = bandPct("RED", cStart, cEnd, cTotal);
    const pRed = bandPct("RED", pStart, pEnd, pTotal);

    return {
      cTotal, pTotal, change, pctChange,
      periodLabel: comparisonMode === "wow" ? "week" : "month",
      greenShift: cGreen - pGreen,
      redShift: cRed - pRed,
      cGreen, cRed,
      cRange: `${format(cStart, "MMM d")} – ${format(cEnd, "MMM d, yyyy")}`,
      pRange: `${format(pStart, "MMM d")} – ${format(pEnd, "MMM d, yyyy")}`,
    };
  }, [trendRows, comparisonMode, startDate, endDate]);

  const shiftLabel = (shift: number, goodWhenNeg: boolean) => {
    if (shift === 0) return null;
    const isGood = goodWhenNeg ? shift < 0 : shift > 0;
    const color = isGood ? "text-green-600 dark:text-green-400" : "text-destructive";
    return (
      <span className={`text-xs ${color}`}>
        {shift > 0 ? "+" : ""}{shift}pp
      </span>
    );
  };

  return (
    <div className="mt-5 pt-5 border-t" data-testid="comparison-stats">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Period comparison</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ToggleChip active={comparisonMode === "wow"} onClick={() => onModeChange("wow")} testId="toggle-comparison-wow">
            Week over week
          </ToggleChip>
          <ToggleChip active={comparisonMode === "mom"} onClick={() => onModeChange("mom")} testId="toggle-comparison-mom">
            Month over month
          </ToggleChip>
        </div>
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground py-2">Loading comparison data…</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-center">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Current</div>
            <div className="text-xl font-bold" data-testid="text-current-total">{stats.cTotal.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.cRange}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Prior {stats.periodLabel === "week" ? "week" : "month"}</div>
            <div className="text-xl font-bold" data-testid="text-previous-total">{stats.pTotal.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.pRange}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">% GREEN</div>
            <div className="text-xl font-bold text-green-700 dark:text-green-400">{stats.cGreen}%</div>
            <div className="text-xs text-muted-foreground">{shiftLabel(stats.greenShift, false) ?? "no change"}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">% RED</div>
            <div className="text-xl font-bold text-destructive">{stats.cRed}%</div>
            <div className="text-xs text-muted-foreground">{shiftLabel(stats.redShift, true) ?? "no change"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Card Header Toggle Row ───────────────────────────────────────────────────

function ToggleChip({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors cursor-pointer ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-muted-foreground border-border hover-elevate"
      }`}
    >
      {children}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export interface BehaviorAnalyticsProps {
  selectedLocation?: string;
  isSystemView: boolean;
  staleLocationCount?: number;
  days: number;
  onDaysChange: (days: number) => void;
  showHints?: boolean;
  onShowHintsChange?: (v: boolean) => void;
}

export function BehaviorAnalytics({ selectedLocation, isSystemView, staleLocationCount, days, onDaysChange, showHints, onShowHintsChange }: BehaviorAnalyticsProps) {
  const resolvedShowHints = showHints ?? false;
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>("wow");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [groupMode, setGroupMode] = useState<GroupMode>("band");
  const [metricMode, setMetricMode] = useState<MetricMode>("interactions");
  const [locationMetric, setLocationMetric] = useState<LocationMetric>("redPct");
  const [coverageWindow, setCoverageWindow] = useState<7 | 14>(14);
  const [tableMode, setTableMode] = useState<"not-assessed" | "assessed">("not-assessed");

  const scope: "system" | "location" = isSystemView ? "system" : "location";
  const locationCode = isSystemView ? undefined : selectedLocation;
  const scopeLabel = isSystemView ? "System-wide" : (selectedLocation || "Unknown");

  const { startDate, endDate } = useMemo(() => getDateRangeForPeriod(String(days)), [days]);
  const dateRangeLabel = useMemo(() => formatDateRange(startDate, endDate), [startDate, endDate]);

  const comparisonDays = COMPARISON_OPTIONS.find((o) => o.value === comparisonMode)?.days ?? 0;
  const fetchDays = Math.min(days + comparisonDays, 365);

  const { data: trendData, isLoading: trendLoading, error: trendError } = useBehaviorScoreTrend(fetchDays, scope, locationCode);
  const { data: topScoresData, isLoading: scoresLoading, error: scoresError } = useBehaviorTopScores(days, scope, locationCode);
  const speciesFilter = "DOG";
  const { data: silentData, isLoading: silentLoading } = useSilentAnimals(days, scope, locationCode, coverageWindow, speciesFilter);
  const { data: coverageData, isLoading: coverageLoading, error: coverageError } = useCoverageTrend(days, scope, locationCode, coverageWindow, speciesFilter);
  const { data: silentList, isLoading: silentListLoading } = useSilentAnimalList(scope, locationCode, coverageWindow, speciesFilter);
  const { data: assessedList, isLoading: assessedListLoading } = useAssessedAnimalList(scope, locationCode, coverageWindow, speciesFilter);
  const { data: locationTrendsData, isLoading: locationTrendsLoading } = useLocationTrends(days);

  // Filter trend rows to the current period only (for charts)
  const currentPeriodStart = startDate.toISOString().slice(0, 10);
  const currentPeriodEnd = endDate.toISOString().slice(0, 10);
  const currentPeriodRows = useMemo(
    () => (trendData?.rows ?? []).filter((r) => r.snapshotDate >= currentPeriodStart && r.snapshotDate <= currentPeriodEnd),
    [trendData?.rows, currentPeriodStart, currentPeriodEnd]
  );

  const handleDaysChange = (newDays: number) => {
    onDaysChange(newDays);
  };

  const sections = isSystemView ? BEHAVIOR_SECTIONS_SYSTEM : BEHAVIOR_SECTIONS_LOCATION;

  return (
    <div className="space-y-6" data-testid="panel-behavior-analytics">
      <InsightsSectionNav
        days={days}
        onDaysChange={handleDaysChange}
        showHints={onShowHintsChange != null ? resolvedShowHints : undefined}
        onShowHintsChange={onShowHintsChange}
        sections={sections}
        showDateRange
      />

      {/* Score Overview */}
      <section id="section-score-overview">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle data-testid="text-overview-title">Score Overview</CardTitle>
                  <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
                </div>
                <CardDescription>{dateRangeLabel}</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <ToggleChip active={metricMode === "interactions"} onClick={() => setMetricMode("interactions")} testId="toggle-metric-interactions">
                    Interactions
                  </ToggleChip>
                  <ToggleChip active={metricMode === "animals"} onClick={() => setMetricMode("animals")} testId="toggle-metric-animals">
                    <Users className="h-3 w-3" />
                    Animals
                  </ToggleChip>
                </div>
                <p className="text-xs text-muted-foreground text-right">
                  {metricMode === "interactions"
                    ? "Each assessment event counted separately — reflects where staff time is going"
                    : "Each animal counted once — reflects how many individuals are in each band"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {resolvedShowHints && (
              <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The bar shows the proportion of the selected metric that falls in each color band across the date range. Switching between Interactions and Animals modes will produce different percentages when assessment frequency varies across animals — for example, if one RED-band animal was assessed 10 times while 9 GREEN animals were assessed once each, Interactions shows RED at ~53% while Animals shows RED at 10%.
              </p>
            )}
            {scoresError ? (
              <div className="text-center py-8 text-destructive">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Failed to load score data</p>
              </div>
            ) : (
              <OverviewStatCards
                topScoresData={topScoresData}
                isLoading={scoresLoading}
                dateRangeLabel={dateRangeLabel}
                metricMode={metricMode}
                scopeLabel={scopeLabel}
                isSystemView={isSystemView}
                showHints={resolvedShowHints}
                silentData={silentData}
                silentLoading={silentLoading}
              />
            )}
            <ComparisonStatsPanel
              trendRows={trendData?.rows ?? []}
              comparisonMode={comparisonMode}
              onModeChange={setComparisonMode}
              startDate={startDate}
              endDate={endDate}
              scopeLabel={scopeLabel}
              isLoading={trendLoading}
            />
          </CardContent>
        </Card>
      </section>

      {/* Score Trend */}
      <section id="section-score-trend">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle data-testid="text-trend-title">Behavior Score Trend</CardTitle>
                  <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
                </div>
                <CardDescription>Daily {metricMode} by score {groupMode}</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <ToggleChip active={chartType === "bar"} onClick={() => setChartType("bar")} testId="toggle-chart-bar">
                    <BarChart3 className="h-3 w-3" />
                    Bar
                  </ToggleChip>
                  <ToggleChip active={chartType === "area"} onClick={() => setChartType("area")} testId="toggle-chart-area">
                    <Activity className="h-3 w-3" />
                    Area
                  </ToggleChip>
                  <div className="w-px h-4 bg-border" />
                  <ToggleChip active={groupMode === "band"} onClick={() => setGroupMode("band")} testId="toggle-group-band">
                    Band
                  </ToggleChip>
                  <ToggleChip active={groupMode === "code"} onClick={() => setGroupMode("code")} testId="toggle-group-code">
                    Code
                  </ToggleChip>
                  <div className="w-px h-4 bg-border" />
                  <ToggleChip active={metricMode === "interactions"} onClick={() => setMetricMode("interactions")} testId="toggle-trend-interactions">
                    Interactions
                  </ToggleChip>
                  <ToggleChip active={metricMode === "animals"} onClick={() => setMetricMode("animals")} testId="toggle-trend-animals">
                    Animals
                  </ToggleChip>
                </div>
                <p className="text-xs text-muted-foreground text-right">
                  {metricMode === "interactions"
                    ? groupMode === "band"
                      ? "Daily interaction count stacked by score band (GREEN / YELLOW / RED)"
                      : "Daily interaction count broken down by individual score code"
                    : groupMode === "band"
                      ? "Daily distinct animal count stacked by score band — each animal counted once per day"
                      : "Daily distinct animal count broken down by individual score code"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {resolvedShowHints && (
              <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                Each column (or area slice) represents one day's data. <strong>Band</strong> grouping stacks GREEN, YELLOW, and RED together; <strong>Code</strong> grouping breaks each band into its individual score codes. <strong>Bar</strong> view shows each day as a discrete column; <strong>Area</strong> view shows the same data as a continuous stacked surface. In <strong>Animals</strong> mode each animal is counted once per day regardless of how many assessments they received that day.
              </p>
            )}
            {trendError ? (
              <div className="text-center py-8 text-destructive">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Failed to load trend data</p>
              </div>
            ) : (
              <ScoreTrendChart
                rows={currentPeriodRows as any}
                isLoading={trendLoading}
                chartType={chartType}
                groupMode={groupMode}
                metricMode={metricMode}
              />
            )}
          </CardContent>
        </Card>
      </section>

      {/* Health Trend */}
      <section id="section-health-trend">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>Behavior Health Trend</CardTitle>
              <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
            </div>
            <CardDescription>Daily % of interactions in RED band — higher is worse</CardDescription>
          </CardHeader>
          <CardContent>
            {resolvedShowHints && (
              <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                Each point on the line shows the percentage of that day's interactions that received a RED-band score (4A, 4B, 5A, or 5B). The value is calculated from interaction counts only, not distinct animals.
              </p>
            )}
            {trendError ? (
              <div className="text-center py-8 text-destructive">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Failed to load health trend data</p>
              </div>
            ) : (
              <HealthTrendChart rows={currentPeriodRows as any} isLoading={trendLoading} />
            )}
          </CardContent>
        </Card>
      </section>

      {/* Per-Location Trend (system view only) */}
      {isSystemView && (
        <section id="section-location-trend">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle>Per-Location Trends</CardTitle>
                    <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
                  </div>
                  <CardDescription>Compare behavior health across shelter locations</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <ToggleChip active={locationMetric === "redPct"} onClick={() => setLocationMetric("redPct")} testId="toggle-location-redpct">
                    % RED
                  </ToggleChip>
                  <ToggleChip active={locationMetric === "interactions"} onClick={() => setLocationMetric("interactions")} testId="toggle-location-interactions">
                    Interactions
                  </ToggleChip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {resolvedShowHints && (
                <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                  In <strong>% RED</strong> mode each line shows the daily percentage of that location's interactions that fell in the RED band. In <strong>Interactions</strong> mode each line shows the raw daily interaction count for that location. Click the legend pills to show or hide individual locations.
                </p>
              )}
              <LocationTrendChart
                locationTrendsData={locationTrendsData}
                isLoading={locationTrendsLoading}
                locationMetric={locationMetric}
              />
            </CardContent>
          </Card>
        </section>
      )}

      {/* Interaction Volume / Coverage Trend */}
      <section id="section-interaction-volume">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle>Interaction Volume (Dogs)</CardTitle>
                  <Badge variant="outline" className="text-xs font-normal">{scopeLabel}</Badge>
                </div>
                <CardDescription>
                  Dogs in care each day vs. those with no behavior interaction in the prior {coverageData?.windowDays ?? coverageWindow} days
                </CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <ToggleChip active={tableMode === "not-assessed"} onClick={() => setTableMode("not-assessed")} testId="toggle-table-not-assessed">
                    Not assessed
                  </ToggleChip>
                  <ToggleChip active={tableMode === "assessed"} onClick={() => setTableMode("assessed")} testId="toggle-table-assessed">
                    Assessed
                  </ToggleChip>
                  <Select
                    value={String(coverageWindow)}
                    onValueChange={(v) => setCoverageWindow(Number(v) as 7 | 14)}
                  >
                    <SelectTrigger className="w-auto" data-testid="select-coverage-window">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Prior 7 days</SelectItem>
                      <SelectItem value="14">Prior 14 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground text-right">
                  {tableMode === "not-assessed"
                    ? `Amber line = dogs in care with no interaction in the prior ${coverageData?.windowDays ?? coverageWindow} days`
                    : `Amber line = dogs in care and assessed in the prior ${coverageData?.windowDays ?? coverageWindow} days`}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {resolvedShowHints && (
              <p className="mb-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-md p-2">
                The dashed line shows the total number of dogs in care on each snapshot date. The solid amber line shows {tableMode === "not-assessed" ? `how many of those dogs had no behavior interaction recorded in the prior ${coverageData?.windowDays ?? coverageWindow} days` : `how many of those dogs had at least one behavior interaction recorded in the prior ${coverageData?.windowDays ?? coverageWindow} days`}. Dogs only — cats are excluded from coverage metrics.
              </p>
            )}
            {coverageError ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive opacity-70" />
                <p className="text-sm text-destructive">Could not load coverage data — the server may be temporarily unavailable. Try refreshing.</p>
              </div>
            ) : (
              <CoverageTrendChart
                series={coverageData?.series}
                isLoading={coverageLoading}
                windowDays={coverageData?.windowDays ?? coverageWindow}
                mode={tableMode}
              />
            )}
            <SilentAnimalTable
              animals={tableMode === "not-assessed" ? silentList : assessedList}
              isLoading={tableMode === "not-assessed" ? silentListLoading : assessedListLoading}
              windowDays={coverageData?.windowDays ?? coverageWindow}
              mode={tableMode}
              noData={!coverageData?.series?.length}
            />
          </CardContent>
        </Card>
      </section>

      {/* Score Breakdown */}
      <section id="section-score-breakdown">
        {scoresError ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-destructive">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Failed to load score data</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <ScoreBreakdown
            scores={topScoresData?.scores ?? []}
            isLoading={scoresLoading}
            metricMode={metricMode}
            dateRangeLabel={dateRangeLabel}
            showHints={resolvedShowHints}
            scopeLabel={scopeLabel}
          />
        )}
      </section>

      {/* Alerts Summary */}
      <section id="section-alerts-summary">
        <div className="space-y-2">
          <div>
            <h3 className="text-base font-semibold">Behavior Alerts (Dogs)</h3>
            <p className="text-sm text-muted-foreground">Dogs flagged for concerning behavior patterns</p>
          </div>
          <BehaviorAlertsSummary
            selectedLocation={selectedLocation}
            isSystemView={isSystemView}
            showHints={showHints}
          />
        </div>
      </section>
    </div>
  );
}
